import { useAppSelector, useAppDispatch } from 'hooks';
import { useFeatureFlag } from 'hooks/useFeatureFlag';
import { useContext } from 'react';

import { Text, SimpleGrid, Flex, Box, Button } from '@chakra-ui/react';

import Card from 'components/card/Card';

import FormInput from 'components/fields/auto/FormInput';
import RadioInput from 'components/fields/auto/RadioInput';
import type { MessageProps } from 'components/fields/shared/Message/Message.types';

import {
  FILE_TYPE,
  isNonOntarioProvince,
  PROVINCE_NEW_BRUNSWICK,
  WIP_STATUS_APPLICATION_CREATION,
  WIP_STATUS_DOCUMENT_GENERATION,
} from 'variables/constants';
import { SECTIONS } from 'views/auto_form/constants';

import getRadioYesNo from 'helpers/getRadioYesNo';

import formatEmail from 'helpers/formatEmail';
import isValidEmail from 'helpers/isValidEmail';
import getValidationMessage from 'helpers/getValidationMessage';

import { ApplicationFormContext } from 'contexts/ApplicationFormContext';

import {
  changeApplicant,
  changeCoApplicant,
  addAdditionalInsured,
  changeAdditionalInsured,
  removeAdditionalInsured,
} from 'reducers/autoPersonalFormReducer';

const Applicant = () => {
  const dispatch = useAppDispatch();

  const { validations } = useContext(ApplicationFormContext) as {
    validations: Validation[];
  };

  const applicant = useAppSelector(
    (state) => state.autoPersonalForm.data.applicant,
  );

  const coApplicant = useAppSelector(
    (state) => state.autoPersonalForm.data.co_applicant,
  );

  const province = useAppSelector((state) => state.autoPersonalForm.province);
  const isNewBrunswick = province === PROVINCE_NEW_BRUNSWICK;

  // Check if validation 112 (Telematics) is triggered
  const validation112 = getValidationMessage(validations, '112');

  const onApplicantChange = (event: any) => {
    const { name, value } = event.target;

    const formattedValue = name === 'email' ? formatEmail(value) : value;

    dispatch(changeApplicant({ name, value: formattedValue }));
  };

  const onCoApplicantChange = (event: any) => {
    const { name, value } = event.target;

    const formattedValue = name === 'email' ? formatEmail(value) : value;

    dispatch(changeCoApplicant({ name, value: formattedValue }));
  };

  const additionalInsureds = useAppSelector(
    (state) => state.autoPersonalForm.data.additional_insureds ?? [],
  );

  const wipStatus = useAppSelector(
    (state) => state.autoPersonalForm.wip_status,
  );

  const hasCoApplicant =
    !!coApplicant.first_name?.trim() && !!coApplicant.last_name?.trim();

  const onAdditionalInsuredChange = (index: number, event: any) => {
    const { name, value } = event.target;
    const formattedValue = name === 'email' ? formatEmail(value) : value;
    dispatch(changeAdditionalInsured({ index, name, value: formattedValue }));
  };

  const isInsuredFilled = (insured: {
    first_name?: string;
    last_name?: string;
  }) => !!insured.first_name?.trim() && !!insured.last_name?.trim();

  const lastInsuredFilled =
    additionalInsureds.length > 0 &&
    isInsuredFilled(additionalInsureds[additionalInsureds.length - 1]);

  const isAdditionalInsuredsEnabled = useFeatureFlag(
    'enable-additional-insureds',
  );
  const canModifyInsureds = [
    WIP_STATUS_APPLICATION_CREATION,
    WIP_STATUS_DOCUMENT_GENERATION,
  ].includes(wipStatus);

  const canAddInsured =
    isAdditionalInsuredsEnabled &&
    canModifyInsureds &&
    hasCoApplicant &&
    additionalInsureds.length < 2 &&
    (additionalInsureds.length === 0 || lastInsuredFilled);

  // Address: prefer 008b, then 008a, then 008 so the correct message and file type show
  const FILE_TYPES_008 = {
    '008a': FILE_TYPE.TYPE_008a,
    '008b': FILE_TYPE.TYPE_008b,
  } as const;
  const activeAddressValidation =
    validations.find(
      (v) => v.validation_id === '008b' && v.validation_outcome !== 'pass',
    ) ??
    validations.find(
      (v) => v.validation_id === '008a' && v.validation_outcome !== 'pass',
    );

  let addressMessage: Omit<MessageProps, 'section'>;
  if (!activeAddressValidation) {
    addressMessage = {
      validationId: '008',
      file: FILE_TYPE.TYPE_008,
      compact: true,
      hideRemarks: false,
    };
  } else {
    const vid = activeAddressValidation.validation_id as '008a' | '008b';
    const { requires_file, requires_remarks } =
      activeAddressValidation.validation_outcome_data;
    const showFile = requires_file ?? true;
    const showRemarks = requires_remarks ?? false;
    addressMessage = {
      validationId: vid,
      file: showFile ? FILE_TYPES_008[vid] : undefined,
      compact: true,
      hideRemarks: !showRemarks,
      ...(showFile && showRemarks
        ? { compactLabel: 'Add remark / Upload file' }
        : {}),
    };
  }

  // Check if applicant and co-applicant emails match
  const validateApplicantEmails = () => {
    const applicantEmail = applicant.email?.toLowerCase().trim();
    const coApplicantEmail = coApplicant.email?.toLowerCase().trim();

    if (
      applicantEmail &&
      coApplicantEmail &&
      applicantEmail === coApplicantEmail
    ) {
      return 'E-signing will not be available if both applicants use the same email';
    }

    return null;
  };

  // Check additional insured email against all other signing party emails
  const validateAdditionalInsuredEmail = (email: string, index: number) => {
    const normalized = email?.toLowerCase().trim();
    if (!normalized) return null;

    const otherEmails = [
      applicant.email?.toLowerCase().trim(),
      coApplicant.email?.toLowerCase().trim(),
      ...additionalInsureds
        .filter((_, i) => i !== index)
        .map((ins) => ins.email?.toLowerCase().trim()),
    ].filter(Boolean);

    if (otherEmails.includes(normalized)) {
      return 'E-signing will not be available if additional insured shares the same email as another signer';
    }

    return null;
  };

  return (
    <Card mb="20px" id="sectionApplicant">
      <Flex justifyContent="space-between" alignItems="center" mb="20px">
        <Text fontWeight="bold" fontSize="2xl">
          Applicant Information
        </Text>
        {canAddInsured && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => dispatch(addAdditionalInsured())}
          >
            + Add Additional Insured
          </Button>
        )}
      </Flex>

      <Box mb="20px">
        <SimpleGrid columns={4} spacing="20px">
          <Box>
            <FormInput
              section={SECTIONS.APPLICANT}
              label="Email"
              name="email"
              value={applicant.email}
              onChange={onApplicantChange}
              required={!!validation112}
            />
            {!isValidEmail(applicant.email) && (
              <Text color="red.500" fontSize="sm" mt={1}>
                Please enter a valid email address
              </Text>
            )}
          </Box>
          <FormInput
            section={SECTIONS.APPLICANT}
            label="First Name"
            name="first_name"
            value={applicant.first_name}
            onChange={onApplicantChange}
            required
            disabled
          />
          <FormInput
            section={SECTIONS.APPLICANT}
            label="Middle Name"
            name="middle_name"
            value={applicant.middle_name}
            onChange={onApplicantChange}
            disabled
          />
          <FormInput
            section={SECTIONS.APPLICANT}
            label="Last Name"
            name="last_name"
            value={applicant.last_name}
            onChange={onApplicantChange}
            required
            disabled
          />
        </SimpleGrid>
      </Box>

      <Box mb="20px">
        <SimpleGrid columns={4} spacing="20px">
          <FormInput
            section={SECTIONS.APPLICANT}
            label="Address"
            name="address"
            value={applicant.address}
            onChange={onApplicantChange}
            message={addressMessage}
            required
            disabled
          />
          <FormInput
            section={SECTIONS.APPLICANT}
            label="City"
            name="city"
            value={applicant.city}
            onChange={onApplicantChange}
            required
            disabled
          />
          <FormInput
            section={SECTIONS.APPLICANT}
            label="Province Code"
            name="province_code"
            value={applicant.province_code}
            onChange={onApplicantChange}
            required
            disabled
          />
          <FormInput
            section={SECTIONS.APPLICANT}
            label="Postal Code"
            name="postal_code"
            value={applicant.postal_code}
            onChange={onApplicantChange}
            required
            disabled
          />
        </SimpleGrid>
      </Box>

      {isNonOntarioProvince(province) && (
        <Box mb="20px">
          <RadioInput
            section={SECTIONS.APPLICANT}
            label={
              isNewBrunswick
                ? 'Is a different address being used for rating/credit purposes?'
                : 'Has applicant changed address within last 3 years?'
            }
            options={[
              { value: 'true', label: 'Yes' },
              { value: 'false', label: 'No' },
            ]}
            value={getRadioYesNo(applicant.has_changed_address_last_3_years)}
            onChange={(value) =>
              onApplicantChange({
                target: {
                  name: 'has_changed_address_last_3_years',
                  value: value === 'true',
                },
              })
            }
            message={
              isNewBrunswick
                ? {
                    validationId: 'credit_rating_address',
                    manual: {
                      show: applicant.has_changed_address_last_3_years === true,
                      message:
                        'Please provide remarks explaining why a different address is used for rating/credit purposes.',
                    },
                  }
                : undefined
            }
            required
          />

          {applicant.has_changed_address_last_3_years && (
            <>
              <SimpleGrid columns={2} spacing="20px" mt="10px">
                <FormInput
                  section={SECTIONS.APPLICANT}
                  label={isNewBrunswick ? 'Address' : 'Previous Address Line 1'}
                  name="previous_address_1"
                  value={applicant.previous_address_1 || ''}
                  onChange={onApplicantChange}
                  required
                />
                <FormInput
                  section={SECTIONS.APPLICANT}
                  label={
                    isNewBrunswick
                      ? 'Address Line 2'
                      : 'Previous Address Line 2'
                  }
                  name="previous_address_2"
                  value={applicant.previous_address_2 || ''}
                  onChange={onApplicantChange}
                />
              </SimpleGrid>
            </>
          )}
        </Box>
      )}

      <Box mb="20px">
        <Flex gap="20px" flexWrap="wrap">
          <Flex gap="10px" alignItems="end">
            <Box w="70px">
              <FormInput
                section={SECTIONS.APPLICANT}
                label="Prefix"
                name="phone_home_prefix"
                value={applicant.phone_home_prefix}
                onChange={onApplicantChange}
                required
                disabled
              />
            </Box>
            <Box w="200px">
              <FormInput
                section={SECTIONS.APPLICANT}
                label="Phone No. Home"
                name="phone_home"
                value={applicant.phone_home}
                onChange={onApplicantChange}
                required
                disabled
              />
            </Box>
          </Flex>
          <Flex gap="10px" alignItems="end">
            <Box w="70px">
              <FormInput
                section={SECTIONS.APPLICANT}
                label="Prefix"
                name="phone_work_prefix"
                value={applicant.phone_work_prefix}
                onChange={onApplicantChange}
                disabled
              />
            </Box>
            <Box w="200px">
              <FormInput
                section={SECTIONS.APPLICANT}
                label="Phone No. Work"
                name="phone_work"
                value={applicant.phone_work}
                onChange={onApplicantChange}
                disabled
              />
            </Box>
          </Flex>
        </Flex>
      </Box>

      <Flex alignItems="center" gap="10px">
        <Text fontWeight="bold" fontSize="xl">
          Co-Applicant Information
        </Text>
      </Flex>

      {hasCoApplicant ? (
        <SimpleGrid columns={4} spacing="20px" mt="20px">
          <Box>
            <FormInput
              section={SECTIONS.APPLICANT}
              label="Email"
              name="email"
              value={coApplicant?.email || ''}
              onChange={onCoApplicantChange}
              required={applicant.email.length > 0 || !!validation112}
            />
            {validateApplicantEmails() && (
              <Text color="red.500" fontSize="sm" mt={1}>
                {validateApplicantEmails()}
              </Text>
            )}
            {!isValidEmail(coApplicant.email) && (
              <Text color="red.500" fontSize="sm" mt={1}>
                Please enter a valid email address
              </Text>
            )}
          </Box>
          <FormInput
            section={SECTIONS.APPLICANT}
            label="First Name"
            name="first_name"
            value={coApplicant?.first_name || ''}
            onChange={onCoApplicantChange}
            disabled
          />
          <FormInput
            section={SECTIONS.APPLICANT}
            label="Middle Name"
            name="middle_name"
            value={coApplicant?.middle_name || ''}
            onChange={onCoApplicantChange}
            disabled
          />
          <FormInput
            section={SECTIONS.APPLICANT}
            label="Last Name"
            name="last_name"
            value={coApplicant?.last_name || ''}
            onChange={onCoApplicantChange}
            disabled
          />
        </SimpleGrid>
      ) : (
        <Box mt="10px">No co-applicant specified on the quote</Box>
      )}

      {isAdditionalInsuredsEnabled &&
        additionalInsureds.map((insured, index) => (
          <Box
            key={index}
            mt="20px"
            p="15px"
            borderWidth="1px"
            borderRadius="md"
            borderColor="gray.200"
          >
            <Flex justifyContent="space-between" alignItems="center" mb="10px">
              <Text fontWeight="medium" fontSize="sm" color="gray.600">
                Additional Insured #{index + 1}
              </Text>
              {canModifyInsureds && (
                <Button
                  size="xs"
                  variant="ghost"
                  colorScheme="red"
                  onClick={() => dispatch(removeAdditionalInsured(index))}
                >
                  Remove
                </Button>
              )}
            </Flex>
            <SimpleGrid columns={4} spacing="20px">
              <Box>
                <FormInput
                  section={SECTIONS.APPLICANT}
                  label="Email"
                  name="email"
                  value={insured.email || ''}
                  onChange={(e: any) => onAdditionalInsuredChange(index, e)}
                />
                {insured.email && !isValidEmail(insured.email) && (
                  <Text color="red.500" fontSize="sm" mt={1}>
                    Please enter a valid email address
                  </Text>
                )}
                {validateAdditionalInsuredEmail(insured.email, index) && (
                  <Text color="red.500" fontSize="sm" mt={1}>
                    {validateAdditionalInsuredEmail(insured.email, index)}
                  </Text>
                )}
              </Box>
              <FormInput
                section={SECTIONS.APPLICANT}
                label="First Name"
                subLabel={`Additional Insured #${index + 1}`}
                name="first_name"
                value={insured.first_name || ''}
                onChange={(e: any) => onAdditionalInsuredChange(index, e)}
                required
              />
              <FormInput
                section={SECTIONS.APPLICANT}
                label="Middle Name"
                name="middle_name"
                value={insured.middle_name || ''}
                onChange={(e: any) => onAdditionalInsuredChange(index, e)}
              />
              <FormInput
                section={SECTIONS.APPLICANT}
                label="Last Name"
                subLabel={`Additional Insured #${index + 1}`}
                name="last_name"
                value={insured.last_name || ''}
                onChange={(e: any) => onAdditionalInsuredChange(index, e)}
                required
              />
            </SimpleGrid>
          </Box>
        ))}
    </Card>
  );
};

export default Applicant;
